import type { SystemStyleObject, Theme } from '@mui/system';

type GenerateTransitionStyleParameters<T> = {
  /** corresponds to `CSSTransition.classNames`, a prefix used for the generated CSS class selectors */
  classNames: T;
  /** CSS transition specification, fallback used for initial and exit if they don't specify explicit transitions */
  transition?: string;
  /** Start state for an entry transition */
  initial: { transition?: string } & SystemStyleObject<Theme>;
  /** Target state for the entry transition, as well as start state for the exit transition */
  animate: SystemStyleObject<Theme>;
  /** Target state for an exit transition, defaults to `initial` if not specified, re-uses `initial.transition` or `transition` if no explicit transition is supplied  */
  exit?: { transition?: string } & SystemStyleObject<Theme>;
};

/**
 * Generate the required CSS transition styles for a React Transition Group transition using `CSSTransition`.
 *
 * entry transition is from `initial` to `animate`
 * exit transition is from `animate` to `exit`, or from `animate` to `initial` if `exit` is not specified
 */
export const generateTransitionStyles = <T extends string>({
  classNames,
  initial,
  animate,
  exit = initial,
  transition,
}: GenerateTransitionStyleParameters<T>): SystemStyleObject<Theme> => {
  const { transition: initialTransition = transition, ...restOfInitial } = initial;
  const { transition: exitTransition = initialTransition, ...restOfExit } = exit ?? initial;

  return {
    [`${classNames}-enter`]: restOfInitial,
    [`${classNames}-enter-active`]: {
      transition: initialTransition,
      ...animate,
    },
    [`${classNames}-exit`]: animate,
    [`${classNames}-exit-active`]: {
      transition: exitTransition,
      ...restOfExit,
    },
  };
};
